import { useEffect, useRef, useState } from "react";
import { useSignupMutation } from "@/hooks/mutations/useSignupMutation";

export const JOINED_KEY = "polygains:early-access:joined";
const DISMISSED_KEY = "polygains:early-access:dismissed";
const WEEK = 7 * 24 * 60 * 60 * 1000;
const OPEN_DELAY_MS = 35_000;

export function readStorage(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

export function writeStorage(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		// Signup works without storage.
	}
}

// One-time popup on the terminal page. Skipped for visitors who already
// signed up or dismissed it within the last week.
export function EarlyAccessModal() {
	const dialog = useRef<HTMLDialogElement>(null);
	const [email, setEmail] = useState("");
	const { signup, isLoading, data, error, reset } = useSignupMutation();
	const close = () => dialog.current?.close();

	useEffect(() => {
		if (readStorage(JOINED_KEY) === "1") return;
		const dismissedAt = Number(readStorage(DISMISSED_KEY));
		if (dismissedAt > 0 && Date.now() - dismissedAt < WEEK) return;
		const timer = window.setTimeout(() => {
			if (
				document.visibilityState === "visible" &&
				!document.querySelector("dialog[open]") &&
				!document.activeElement?.matches(
					"input, textarea, select, [contenteditable]",
				)
			) {
				dialog.current?.showModal();
			}
		}, OPEN_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!email || isLoading) return;
		const result = await signup(email.trim());
		if (result?.success) {
			writeStorage(JOINED_KEY, "1");
			window.setTimeout(close, 1500);
		}
	};

	const showSuccess = data?.success;
	const showError = error || (data && !data.success);

	return (
		<dialog
			ref={dialog}
			className="modal"
			aria-labelledby="early-access-title"
			onClose={() => writeStorage(DISMISSED_KEY, String(Date.now()))}
		>
			<div className="modal-box bg-base-300 border border-primary/40 rounded-md font-mono text-xs md:text-sm p-5 max-w-lg">
				<div className="flex items-center justify-between mb-3">
					<span className="text-primary">$ early_access --join</span>
					<button
						type="button"
						className="btn btn-xs btn-ghost text-base-content/60"
						aria-label="Close"
						onClick={close}
					>
						✕
					</button>
				</div>
				<h2
					id="early-access-title"
					className="text-base md:text-lg font-bold text-base-content mb-2"
				>
					Get in early. Explore what comes next.
				</h2>
				<p className="text-base-content/70 mb-2">
					Spot unusual trading patterns and large positions on Polymarket as
					they happen, all in one terminal.
				</p>
				<p className="text-base-content mb-4">
					Join early for free premium access and beta features when available.
				</p>
				{showSuccess ? (
					<p role="status" className="text-success">
						✓ You're on the list.
					</p>
				) : (
					<form onSubmit={handleSubmit} className="join w-full">
						<input
							type="email"
							required
							maxLength={254}
							autoComplete="email"
							placeholder="mail@site.com"
							aria-label="Email address"
							className={`input join-item w-full min-w-0 ${showError ? "input-error" : ""}`}
							value={email}
							disabled={isLoading}
							onChange={(e) => {
								setEmail(e.target.value);
								if (showError) reset();
							}}
						/>
						<button
							type="submit"
							className="btn btn-primary join-item"
							disabled={isLoading || !email}
						>
							{isLoading ? "..." : "Get early access"}
						</button>
					</form>
				)}
				{showError && (
					<p role="status" className="text-error text-xs mt-2">
						{data?.error || error?.message || "Failed to subscribe"}
					</p>
				)}
				<p className="text-base-content/50 text-[11px] mt-3">
					No payment details required. Launch updates only.
				</p>
			</div>
			<form method="dialog" className="modal-backdrop">
				<button type="submit" aria-label="Close">
					close
				</button>
			</form>
		</dialog>
	);
}
